import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
// components
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Checkbox,
  FormControlLabel,
  Typography,
} from "@mui/material";
import SettingTextField from "../../components/SettingTextField";
// services
import { useQueryClient } from "@tanstack/react-query";
// constants
import { ROLE_NAMES } from "../../constants";
import { roleServices } from "../../api/useQueryHooks";

// Validation schema using Zod
const roleSchema = z.object({
  name: z
    .string({ message: "Role name is required" })
    .min(3, "Role name must be at least 3 characters long"),
  permissions: z
    .array(z.string())
    .nonempty("At least one permission is required"),
});

const RolePopup = ({ open, onClose, permissionsData, setMsgAlert }) => {
  const queryClient = useQueryClient();
  const [selectedPermissions, setSelectedPermissions] = useState([]);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState,
    trigger,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(roleSchema),
    defaultValues: {
      name: "",
      permissions: [],
    },
  });

  const createMutation = roleServices.useCreateRole(
    () => {
      setMsgAlert({
        open: true,
        message: "Role created successfully!",
        msgType: "success",
      });
      reset();
      queryClient.invalidateQueries(["getAllRoles"]);
      onClose();
    },
    (error) => {
      setMsgAlert({
        open: true,
        message: `Error creating role: ${error.message}`,
        msgType: "error",
      });
    }
  );
  const updateMutation = roleServices.useUpdateRole(
    () => {
      setMsgAlert({
        open: true,
        message: "Role updated successfully!",
        msgType: "success",
      });
      reset();
      queryClient.invalidateQueries(["getAllRoles"]);
      onClose();
    },
    (error) => {
      setMsgAlert({
        open: true,
        message: `Error updating role: ${error.message}`,
        msgType: "error",
      });
    }
  );

  // Populate form when editing
  useEffect(() => {
    if (open?.type === "edit" && open?.data) {
      reset({
        name: open?.data?.name || "",
        permissions: open?.data?.permissions?.map((item) => item?.type) || [],
      });
      setSelectedPermissions(
        open?.data?.permissions?.map((item) => item?.type) || []
      );
    } else {
      reset({
        name: "",
        permissions: [],
      });
      setSelectedPermissions([]);
    }
  }, [open, reset]);

  const handleGroupCheckboxChange = (groupPermissions, isChecked) => {
    const updatedPermissions = isChecked
      ? [...new Set([...selectedPermissions, ...groupPermissions])]
      : selectedPermissions.filter((perm) => !groupPermissions.includes(perm));
    setSelectedPermissions(updatedPermissions);
    setValue("permissions", updatedPermissions, { shouldDirty: true }); // Mark form as dirty
    trigger("permissions"); // Trigger validation for the permissions field
  };

  const handlePermissionCheckboxChange = (permission, isChecked) => {
    const updatedPermissions = isChecked
      ? [...selectedPermissions, permission]
      : selectedPermissions.filter((perm) => perm !== permission);
    setSelectedPermissions(updatedPermissions);
    setValue("permissions", updatedPermissions, { shouldDirty: true }); // Mark form as dirty
    trigger("permissions"); // Trigger validation for the permissions field
  };

  const onSubmit = (data) => {
    if (open?.type === "edit" && open?.data) {
      updateMutation.mutate({
        id: open?.data?._id,
        data: {
          name: data?.name,
          permissions: selectedPermissions,
        },
      });
    } else {
      createMutation.mutate({
        name: data?.name,
        permissions: selectedPermissions,
      });
    }
  };

  return (
    <Dialog
      open={!!open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      sx={{ "& .MuiPaper-root": { padding: "2.5rem" } }}
    >
      <DialogTitle sx={{ pl: 0 }}>
        {open?.type === "edit" ? "Edit Role" : "Add New Role"}
      </DialogTitle>
      <DialogContent sx={{ padding: 0 }}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Controller
            name="name"
            control={control}
            render={({ field, fieldState }) => (
              <>
                <SettingTextField
                  {...field}
                  editable={
                    open?.data?.name !== ROLE_NAMES.superAdmin ? true : false
                  }
                  label={"Role Name"}
                  value={field.value}
                  setValue={field.onChange}
                  placeholder={"Enter role name"}
                  required={true}
                  variant={"filled"}
                  border={"1px solid #AAA"}
                  // error={!!fieldState.error}
                  borderRadius={"5px"}
                  error={fieldState?.error?.message}
                />
              </>
            )}
          />
          {errors?.permissions && (
            <Typography
              color="error"
              sx={{
                mt: 2,
                mb: -1,
                fontSize: "0.75rem",
                height: "1.125rem",
                fontFamily: "GilroySemiBold",
                visibility: errors?.permissions ? "visible" : "hidden",
              }}
            >
              {errors?.permissions?.message}
            </Typography>
          )}
          {permissionsData.map((group) => (
            <div
              key={group.group}
              style={{
                backgroundColor: "#f9f9f9",
                padding: "8px",
                marginTop: "16px",
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    sx={{ color: "black", "&.Mui-checked": { color: "black" } }}
                    checked={group.permissions.every((perm) =>
                      selectedPermissions.includes(perm.type)
                    )}
                    onChange={(e) =>
                      handleGroupCheckboxChange(
                        group.permissions.map((perm) => perm.type),
                        e.target.checked
                      )
                    }
                  />
                }
                label={group.title}
              />
              <div style={{ marginLeft: "16px" }}>
                {group.permissions.map((permission) => (
                  <FormControlLabel
                    sx={{ alignItems: "flex-start" }}
                    key={permission.type}
                    control={
                      <Checkbox
                        label={permission.name}
                        sx={{
                          color: "black",
                          "&.Mui-checked": { color: "black" },
                          ml: 0,
                          mr: 1,
                          padding: 0,
                          my: 0.2,
                          "& .MuiTypography-root": { fontSize: "0.875rem" },
                        }}
                        checked={selectedPermissions.includes(permission.type)}
                        onChange={(e) =>
                          handlePermissionCheckboxChange(
                            permission.type,
                            e.target.checked
                          )
                        }
                      />
                    }
                    label={permission.name}
                  />
                ))}
              </div>
            </div>
          ))}
        </form>
      </DialogContent>
      <DialogActions sx={{ marginTop: "1rem", padding: 0 }}>
        <Button
          onClick={onClose}
          type="button"
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#ffffff !important",
            border: "2px solid #000000 !important",
            width: "100%",
            height: "48px",
            borderRadius: "8px !important",
            padding: "0 18px",
            color: "#000000 !important",
            fontSize: "14px !important",
            fontFamily: "GilroySemiBold !important",
            cursor: "pointer",
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          type="button"
          disabled={open?.type === "edit" && !formState.isDirty}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#000000 !important",
            width: "100%",
            height: "48px",
            borderRadius: "8px !important",
            padding: "0 18px",
            color: "#ffffff !important",
            fontSize: "14px !important",
            fontFamily: "GilroySemiBold !important",
            cursor: formState.isDirty ? "pointer" : "not-allowed", // Cursor not allowed when disabled
            opacity: formState.isDirty ? 1 : 0.6, // Reduce opacity when disabled
            marginLeft: "10px !important",
          }}
        >
          {createMutation.isPending || updateMutation.isPending
            ? "Saving..."
            : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RolePopup;
